import { Link } from 'react-router-dom';

export default function HomePage() {
    return (
        <div className="relative overflow-hidden">
            {/* Hero Section */}
            <section className="relative min-h-[45vh] flex items-center">
                <div className="absolute inset-0 bg-pirate-gradient-dark" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--pirate-red)_0%,_transparent_50%)] opacity-20" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                    <div className="text-center animate-fade-in">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-500 rounded-2xl mb-5 shadow-pirate-lg">
                            <span className="text-white font-display font-extrabold text-4xl">🏴‍☠️</span>
                        </div>
                        <div className="inline-block bg-primary-950/80 border border-primary-500/40 px-4 py-1 rounded-full text-primary-300 font-semibold text-xs uppercase tracking-widest mb-3">
                            Peck Community Schools • Est. 2024
                        </div>
                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-extrabold text-white mb-3">
                            Peck <span className="text-gradient">Pirates</span> Esports
                        </h1>
                        <p className="text-lg md:text-xl text-secondary-200 max-w-2xl mx-auto font-medium">
                            Compete. Create. Broadcast. — <span className="text-primary-400">There's a spot for everyone.</span>
                        </p>
                    </div>
                </div>
            </section>

            {/* Fall 2026 Signup Hero Banner */}
            <section className="py-8 bg-gradient-to-r from-primary-700 via-primary-600 to-primary-800 border-y border-primary-500/40 shadow-xl">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                        <div className="text-center lg:text-left">
                            <div className="inline-flex items-center gap-2 bg-yellow-400/20 text-yellow-300 border border-yellow-400/30 px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider mb-2">
                                <span>⚡</span> FALL 2026 SIGNUPS OPEN
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-display font-bold text-white leading-tight">
                                Join the Fall 2026 Esports Team & Crew
                            </h2>
                            <p className="text-primary-100 text-sm sm:text-base mt-1">
                                Open to Grades 8–12 • Mon–Wed 3:00–5:00 PM • Season runs Sept 7 – Dec 5
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center justify-center gap-3">
                            <a
                                href="https://forms.gle/2GvpB888G5xRci1S9"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-yellow-400 text-primary-950 hover:bg-yellow-300 px-6 py-3 rounded-xl font-extrabold text-base transition-all shadow-lg hover:shadow-yellow-400/30 ring-2 ring-yellow-300 flex items-center gap-2"
                            >
                                <span>📝 Complete Signup Form</span>
                                <span className="text-xs">↗</span>
                            </a>
                            <a
                                href="/documents/Peck_Pirates_Esports_Fall_2026_Packet.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-4 py-3 rounded-xl font-semibold text-sm transition-colors flex items-center gap-2"
                            >
                                <span>📄 Season Packet (PDF)</span>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Info Meeting Callout Card */}
            <section className="py-10 bg-secondary-900">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-gradient-to-br from-primary-900/60 via-secondary-800 to-secondary-900 border-2 border-primary-500/40 rounded-2xl p-6 sm:p-8 shadow-2xl">
                        <div className="flex flex-col md:flex-row gap-6 items-center md:items-start justify-between">
                            <div className="flex-1 text-center md:text-left">
                                <div className="inline-flex items-center gap-2 bg-primary-500 text-white px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider mb-3">
                                    📣 Mark Your Calendar
                                </div>
                                <h3 className="text-2xl sm:text-3xl font-display font-bold text-white mb-2">
                                    Parent & Student Info Meeting
                                </h3>
                                <p className="text-secondary-300 text-sm sm:text-base leading-relaxed mb-4">
                                    Students and parents are invited to <strong>check out the Esports Lab</strong>, meet Coach Anklam, try out the gear, and learn all about our competitive and broadcasting programs.
                                </p>
                                <p className="text-primary-300 text-xs sm:text-sm font-semibold">
                                    Students can drop in right after school to check out the lab; parents are welcome to drop in anytime during the window!
                                </p>
                            </div>

                            <div className="bg-secondary-900/90 border border-secondary-700 rounded-xl p-5 w-full md:w-72 shrink-0 space-y-3">
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">📅</span>
                                    <div>
                                        <div className="text-xs uppercase text-secondary-400 font-semibold">Date</div>
                                        <div className="text-white font-bold text-sm">Tuesday, Sept 1, 2026</div>
                                    </div>
                                </div>
                                <div className="border-t border-secondary-800 pt-2 flex items-center gap-3">
                                    <span className="text-2xl">🕒</span>
                                    <div>
                                        <div className="text-xs uppercase text-secondary-400 font-semibold">Open Lab Window</div>
                                        <div className="text-white font-bold text-sm">3:00 PM – 5:00 PM</div>
                                        <div className="text-xs text-primary-400">Brief Presentation @ 4:30 PM</div>
                                    </div>
                                </div>
                                <div className="border-t border-secondary-800 pt-2 flex items-center gap-3">
                                    <span className="text-2xl">🏫</span>
                                    <div>
                                        <div className="text-xs uppercase text-secondary-400 font-semibold">Location</div>
                                        <div className="text-white font-bold text-sm">Esports Lab / Media Center</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Document Links Strip */}
                        <div className="mt-6 pt-6 border-t border-secondary-700/80 flex flex-wrap items-center justify-center md:justify-start gap-3">
                            <span className="text-xs uppercase tracking-wider text-secondary-400 font-bold mr-2">
                                📥 Documents & Info:
                            </span>
                            <a
                                href="/documents/Peck_Pirates_Esports_Fall_2026_Packet.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Download Fall 2026 Season Packet PDF (opens in a new tab)"
                                className="bg-secondary-800 hover:bg-secondary-700 text-secondary-200 text-xs px-3.5 py-2 rounded-lg border border-secondary-600 transition-colors flex items-center gap-1.5 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none"
                            >
                                <span>📄 Season Packet (PDF)</span>
                            </a>
                            <a
                                href="/documents/Code-of-Conduct-Fall-2026.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Download Code of Conduct and Authorization Form PDF (opens in a new tab)"
                                className="bg-secondary-800 hover:bg-secondary-700 text-secondary-200 text-xs px-3.5 py-2 rounded-lg border border-secondary-600 transition-colors flex items-center gap-1.5 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none"
                            >
                                <span>📜 Code of Conduct (PDF)</span>
                            </a>
                            <a
                                href="/documents/fall-2026-info-meeting.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="View Printable Meeting Handout (opens in a new tab)"
                                className="bg-secondary-800 hover:bg-secondary-700 text-secondary-200 text-xs px-3.5 py-2 rounded-lg border border-secondary-600 transition-colors flex items-center gap-1.5 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none"
                            >
                                <span>🖨️ Meeting Handout</span>
                            </a>
                            <Link
                                to="/parents"
                                className="bg-secondary-800 hover:bg-secondary-700 text-yellow-400 text-xs px-3.5 py-2 rounded-lg border border-secondary-600 transition-colors flex items-center gap-1.5 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none"
                            >
                                <span>👨‍👩‍👧 Parents Guide</span>
                            </Link>
                        </div>


                    </div>
                </div>
            </section>

            {/* The Two Tracks (Competitive vs Production) */}
            <section className="py-12 bg-secondary-950">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <div className="inline-block bg-primary-500/20 text-primary-400 border border-primary-500/30 px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider mb-2">
                            Two Ways to Participate
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-display font-bold text-white mb-3">
                            Choose Your Track
                        </h2>
                        <p className="text-secondary-400 max-w-2xl mx-auto text-sm sm:text-base">
                            Whether you want to compete on the sticks or produce professional broadcasts, Peck Esports offers real skills, teamwork, and varsity representation.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Track 1: Competitive Team */}
                        <div className="card border-2 border-secondary-700 hover:border-primary-500/50 transition-all flex flex-col justify-between bg-gradient-to-b from-secondary-800/80 to-secondary-900">
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center text-2xl">
                                        ⚔️
                                    </div>
                                    <span className="bg-primary-500/20 text-primary-300 border border-primary-500/40 text-xs font-bold px-2.5 py-1 rounded-full uppercase">
                                        Competitive Track
                                    </span>
                                </div>
                                <h3 className="text-2xl font-display font-bold text-white mb-2">
                                    Competitive Esports Team
                                </h3>
                                <p className="text-secondary-300 text-sm mb-4">
                                    Represent Peck High School against schools across Michigan in the official Michigan High School Esports Federation (MiHSEF).
                                </p>
                                <div className="space-y-2.5 text-sm text-secondary-300 mb-6">
                                    <div className="flex items-start gap-2">
                                        <span className="text-primary-400 font-bold">✓</span>
                                        <span><strong>Super Smash Bros. Ultimate:</strong> Solos & 3v3 Crews format</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <span className="text-primary-400 font-bold">✓</span>
                                        <span><strong>State Finals Pathway:</strong> Compete for trophies at UM-Flint on Dec 5</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <span className="text-primary-400 font-bold">✓</span>
                                        <span><strong>Additional Titles:</strong> Evaluated based on student interest at Sept 1 meeting</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <span className="text-primary-400 font-bold">✓</span>
                                        <span><strong>Teamwork & Strategy:</strong> Match prep, VOD review, and sportsmanship</span>
                                    </div>
                                </div>
                            </div>
                            <div className="pt-4 border-t border-secondary-700">
                                <Link to="/games" className="btn-secondary w-full text-center text-sm py-2">
                                    View Game Details →
                                </Link>
                            </div>
                        </div>

                        {/* Track 2: Production Team */}
                        <div className="card border-2 border-primary-500/50 hover:border-primary-400 transition-all flex flex-col justify-between bg-gradient-to-b from-primary-950/40 to-secondary-900">
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="w-12 h-12 bg-yellow-400/20 rounded-xl flex items-center justify-center text-2xl">
                                        🎬
                                    </div>
                                    <span className="bg-yellow-400/20 text-yellow-300 border border-yellow-400/40 text-xs font-bold px-2.5 py-1 rounded-full uppercase">
                                        New for Fall 2026
                                    </span>
                                </div>
                                <h3 className="text-2xl font-display font-bold text-white mb-2">
                                    Production & Broadcasting Crew
                                </h3>
                                <p className="text-secondary-300 text-sm mb-4">
                                    Non-competitor roles for students passionate about streaming, video editing, social media, graphics, tech, and live commentary.
                                </p>
                                <div className="space-y-2.5 text-sm text-secondary-300 mb-6">
                                    <div className="flex items-start gap-2">
                                        <span className="text-yellow-400 font-bold">✓</span>
                                        <span><strong>Casters & Commentators:</strong> Live play-by-play and color commentary on mic</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <span className="text-yellow-400 font-bold">✓</span>
                                        <span><strong>Stream Tech Crew:</strong> Run OBS, video cameras, audio switchers & streaming rigs</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <span className="text-yellow-400 font-bold">✓</span>
                                        <span><strong>Graphics & Overlays:</strong> Design broadcast lower-thirds, animations & stats</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <span className="text-yellow-400 font-bold">✓</span>
                                        <span><strong>Social Media & Highlights:</strong> TikTok/YouTube shorts, clip edits & team branding</span>
                                    </div>
                                </div>
                            </div>
                            <div className="pt-4 border-t border-secondary-700">
                                <a
                                    href="/documents/production-team-signups.html"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-primary w-full text-center text-sm py-2 block"
                                >
                                    View Production Roles Guide (PDF/Print) →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Program Policies & Benefits */}
            <section className="py-12 bg-secondary-900 border-t border-secondary-800">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl sm:text-3xl font-display font-bold text-white mb-8 text-center">
                        Why Join Peck Esports?
                    </h2>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="card text-center p-5 bg-secondary-800 border-secondary-700">
                            <div className="text-3xl mb-3" aria-hidden="true">🏃</div>
                            <h3 className="font-bold text-white text-base mb-1">Dual-Sport Friendly</h3>
                            <p className="text-secondary-300 text-xs leading-relaxed">
                                Playing football, cross country, cheer, or another sport? We coordinate directly with your athletic coach so you can do both.
                            </p>
                        </div>

                        <div className="card text-center p-5 bg-secondary-800 border-secondary-700">
                            <div className="text-3xl mb-3" aria-hidden="true">💰</div>
                            <h3 className="font-bold text-white text-base mb-1">No Participation Fees</h3>
                            <p className="text-secondary-300 text-xs leading-relaxed">
                                100% free to join. All lab PCs, consoles, broadcast gear, and regular season league entries are fully covered. (Optional custom jerseys and special travel trips subsidized by team fundraising).
                            </p>
                        </div>

                        <div className="card text-center p-5 bg-secondary-800 border-secondary-700">
                            <div className="text-3xl mb-3" aria-hidden="true">🖥️</div>
                            <h3 className="font-bold text-white text-base mb-1">Pro Esports Lab</h3>
                            <p className="text-secondary-300 text-xs leading-relaxed">
                                6 dedicated gaming PCs with RTX 5060 GPUs, Nintendo Switch tournament setups, and professional broadcast switcher gear.
                            </p>
                        </div>

                        <div className="card text-center p-5 bg-secondary-800 border-secondary-700">
                            <div className="text-3xl mb-3" aria-hidden="true">🚌</div>
                            <h3 className="font-bold text-white text-base mb-1">Trips & State Finals</h3>
                            <p className="text-secondary-300 text-xs leading-relaxed">
                                Opportunities for college campus visits (SC4, Northwood) and competing at the MiHSEF State Finals at UM-Flint.
                            </p>
                        </div>
                    </div>

                </div>
            </section>

            {/* State Finals 2025 Highlights */}
            <section className="py-12 bg-gradient-to-b from-secondary-900 to-secondary-950">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <span className="text-4xl mb-2 block">🏆</span>
                        <h2 className="text-3xl font-display font-bold text-white mb-2">
                            Championship Legacy
                        </h2>
                        <p className="text-secondary-400 text-sm">MIHSEF State Finals • University of Michigan - Flint</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                        <div className="card border-2 border-gray-400/50 bg-gradient-to-br from-gray-400/10 to-transparent text-center p-5">
                            <div className="text-3xl mb-2">🥈</div>
                            <h3 className="text-lg font-bold text-white mb-1">SSBU Crews 3v3</h3>
                            <div className="text-xl font-display font-bold text-gray-300 mb-1">State Runner-Up</div>
                            <div className="text-xs text-secondary-400">
                                2nd in Michigan • Fall State Finals
                            </div>
                        </div>

                        <div className="card border-2 border-orange-500/50 bg-gradient-to-br from-orange-500/10 to-transparent text-center p-5">
                            <div className="text-3xl mb-2">🥉</div>
                            <h3 className="text-lg font-bold text-white mb-1">SSBU Solos</h3>
                            <div className="text-xl font-display font-bold text-orange-400 mb-1">3rd Place State</div>
                            <div className="text-xs text-secondary-400">
                                Individual State Bronze Medalist
                            </div>
                        </div>

                        <div className="card text-center p-5">
                            <div className="text-3xl mb-2">🎓</div>
                            <h3 className="text-lg font-bold text-white mb-1">Varsity Lettering</h3>
                            <div className="text-xl font-display font-bold text-secondary-200 mb-1">High School Credit</div>
                            <div className="text-xs text-secondary-400">
                                Earn your official Peck High School Varsity Letter
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Season Timeline */}
            <section className="py-12 bg-secondary-900 border-t border-secondary-800" aria-labelledby="home-schedule-title">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <h2 id="home-schedule-title" className="text-2xl sm:text-3xl font-display font-bold text-white mb-2">
                            📆 Fall 2026 Season Schedule
                        </h2>
                        <p className="text-secondary-300 text-sm mb-4">
                            Weekly Practices & Matches • Mondays through Wednesdays 3:00–5:00 PM
                        </p>
                        <a
                            href="https://calendar.google.com/calendar/u/0?cid=Y180YzQ3ODY3YTg3ZTczMDNkNDhhZDQ1NjRjODk1MjdiNjA5ZTQ4ODhlMGY3YTcwZDA4N2I3ZTk3OGRiZTA5ZmQxQGdyb3VwLmNhbGVuZGFyLmdvb2dsZS5jb20"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Subscribe to Peck Pirates Esports Google Calendar (opens in a new tab)"
                            className="inline-flex items-center gap-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/40 text-xs px-4 py-2 rounded-lg font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:outline-none"
                        >
                            <span aria-hidden="true">📅</span>
                            <span>Add to Google Calendar</span>
                            <span aria-hidden="true">↗</span>
                        </a>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="card p-4 border-l-4 border-l-yellow-400 bg-secondary-800">
                            <div className="text-xs font-bold text-yellow-400 uppercase">Sept 1</div>
                            <div className="font-bold text-white mt-1">Info Meeting</div>
                            <div className="text-xs text-secondary-300 mt-1">3–5 PM Drop-in (4:30 presentation)</div>
                        </div>

                        <div className="card p-4 border-l-4 border-l-blue-400 bg-secondary-800">
                            <div className="text-xs font-bold text-blue-400 uppercase">Sept 7 – 18</div>
                            <div className="font-bold text-white mt-1">Pre-Season</div>
                            <div className="text-xs text-secondary-300 mt-1">Signups due Sept 14 @ 3 PM</div>
                        </div>

                        <div className="card p-4 border-l-4 border-l-green-400 bg-secondary-800">
                            <div className="text-xs font-bold text-green-400 uppercase">Sept 21 – Nov 6</div>
                            <div className="font-bold text-white mt-1">Regular Season</div>
                            <div className="text-xs text-secondary-300 mt-1">Mon–Wed 3:00–5:00 PM</div>
                        </div>

                        <div className="card p-4 border-l-4 border-l-primary-500 bg-secondary-800">
                            <div className="text-xs font-bold text-primary-400 uppercase">Dec 5</div>
                            <div className="font-bold text-white mt-1">State Finals</div>
                            <div className="text-xs text-secondary-300 mt-1">UM-Flint Championship</div>
                        </div>
                    </div>
                </div>
            </section>


            {/* Bottom Signup Call to Action */}
            <section className="py-14 bg-gradient-to-r from-primary-800 via-primary-700 to-primary-900 text-center">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl sm:text-4xl font-display font-extrabold text-white mb-3">
                        Ready to Join the Crew?
                    </h2>
                    <p className="text-primary-100 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
                        Sign up today to reserve your spot on the team or broadcasting staff. Dual-sport athletes and all skill levels welcome!
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4">
                        <a
                            href="https://forms.gle/2GvpB888G5xRci1S9"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-yellow-400 text-primary-950 hover:bg-yellow-300 px-8 py-3.5 rounded-xl font-extrabold text-base transition-all shadow-xl hover:shadow-yellow-400/40 ring-4 ring-yellow-300/50 flex items-center gap-2"
                        >
                            <span>Fill Out Google Signup Form</span>
                            <span>↗</span>
                        </a>
                        <Link
                            to="/signups"
                            className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-6 py-3.5 rounded-xl font-semibold text-sm transition-colors"
                        >
                            View All Signup Details & Docs
                        </Link>
                    </div>
                </div>
            </section>

            {/* Contact & Coach Info */}
            <section className="py-8 bg-secondary-950 border-t border-secondary-800">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-secondary-300 font-semibold mb-1">Questions? We're here to help!</p>
                    <p className="text-secondary-400 text-sm">
                        Contact Esports Coach <strong className="text-white">Brian Anklam</strong> •
                        <a href="mailto:anklamb@peckschools.net" className="text-primary-400 hover:underline ml-1 font-medium">anklamb@peckschools.net</a>
                        <span className="mx-2 text-secondary-600">•</span>
                        <a href="tel:8103780645" className="text-primary-400 hover:underline font-medium">(810) 378-0645</a>
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-6 bg-black border-t border-secondary-900">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-secondary-500 text-xs">
                        © 2026 Peck Pirates Esports • Peck Community Schools • Michigan High School Esports Federation (MiHSEF)
                    </p>
                </div>
            </footer>
        </div>
    );
}

